import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

import WelcomeHero from "@/components/student/WelcomeHero";
import StatSummaryBento from "@/components/student/StatSummaryBento";
import ContinueLearningCard from "@/components/student/ContinueLearningCard";
import ProgramCard from "@/components/student/ProgramCard";
import MyAttendanceCard from "@/components/student/MyAttendanceCard";
import MomentumRibbon from "@/components/student/MomentumRibbon";
import { certificateConfetti } from "@/lib/celebrate";
import { BookOpen } from "lucide-react";

const CONTAINER = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.09, delayChildren: 0.05 },
    },
};

const ITEM = {
    hidden: { opacity: 0, y: 16 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 320, damping: 30 },
    },
};

function SkeletonCard({ className = "" }) {
    return (
        <div
            className={`rounded-2xl bg-muted/40 border border-primary/5 animate-pulse ${className}`}
        />
    );
}

export default function StudentDashboard() {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [streak, setStreak] = useState(0);

    useEffect(() => {
        api.get("/me/enrollments")
            .then((r) => {
                const list = r.data || [];
                setEnrollments(list);
                // Celebrate any newly-earned certificates we haven't seen before.
                try {
                    const seenRaw = localStorage.getItem("seen_certificates") || "[]";
                    const seen = new Set(JSON.parse(seenRaw));
                    const newCerts = list
                        .map((e) => e.certificate_id)
                        .filter((cid) => cid && !seen.has(cid));
                    if (newCerts.length) {
                        setTimeout(() => certificateConfetti(), 600);
                        newCerts.forEach((cid) => seen.add(cid));
                        localStorage.setItem(
                            "seen_certificates",
                            JSON.stringify([...seen]),
                        );
                    }
                } catch {
                    /* ignore */
                }
            })
            .finally(() => setLoading(false));
    }, []);

    // Lightweight study-streak: count consecutive days (ending today) on which
    // the student accrued >= 10 min on any program. We can derive it later
    // from a dedicated endpoint; for now read from localStorage cache that
    // MyAttendanceCard populates, otherwise stay at 0.
    useEffect(() => {
        try {
            const cached = JSON.parse(
                localStorage.getItem("student_study_streak") || "0",
            );
            if (typeof cached === "number" && cached > 0) setStreak(cached);
        } catch {
            /* noop */
        }
    }, []);

    const sortedEnrollments = useMemo(() => {
        // Active programs first, then completed, stable on progress_percent desc
        const arr = [...enrollments];
        arr.sort((a, b) => {
            const ac = a.completed_at ? 1 : 0;
            const bc = b.completed_at ? 1 : 0;
            if (ac !== bc) return ac - bc;
            return (b.progress_percent || 0) - (a.progress_percent || 0);
        });
        return arr;
    }, [enrollments]);

    return (
        <motion.div
            variants={CONTAINER}
            initial="hidden"
            animate="show"
            className="max-w-7xl mx-auto px-6 lg:px-8 py-10 lg:py-14 space-y-8"
            data-testid="student-dashboard"
        >
            {/* Hero */}
            <WelcomeHero studyStreak={streak} />

            {/* Weekly momentum ribbon — small, dismissable-free, never reflows */}
            <motion.div variants={ITEM}>
                <MomentumRibbon />
            </motion.div>

            {/* Bento: Continue learning (big) + Stats column + Attendance */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <motion.div
                    variants={ITEM}
                    className="lg:col-span-5"
                >
                    {loading ? (
                        <SkeletonCard className="h-full min-h-[280px]" />
                    ) : (
                        <ContinueLearningCard enrollments={sortedEnrollments} />
                    )}
                </motion.div>
                <motion.div
                    variants={ITEM}
                    className="lg:col-span-3"
                >
                    {loading ? (
                        <div className="space-y-4">
                            <SkeletonCard className="h-28" />
                            <SkeletonCard className="h-28" />
                            <SkeletonCard className="h-28" />
                        </div>
                    ) : (
                        <StatSummaryBento enrollments={enrollments} />
                    )}
                </motion.div>
                <motion.div
                    variants={ITEM}
                    className="lg:col-span-4"
                >
                    <MyAttendanceCard
                        enrollments={enrollments}
                        onStreakChange={(s) => {
                            setStreak(s);
                            try {
                                localStorage.setItem(
                                    "student_study_streak",
                                    JSON.stringify(s),
                                );
                            } catch {
                                /* noop */
                            }
                        }}
                    />
                </motion.div>
            </div>

            {/* Programs */}
            <motion.section variants={ITEM} data-testid="my-programs-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                            Your library
                        </div>
                        <h2 className="font-display text-3xl text-primary mt-1 tracking-tight">
                            My programs
                        </h2>
                    </div>
                    {enrollments.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                            {enrollments.length} enrolled
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <SkeletonCard className="h-72" />
                        <SkeletonCard className="h-72" />
                        <SkeletonCard className="h-72" />
                    </div>
                ) : sortedEnrollments.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-primary/15 p-12 text-center bg-card/40">
                        <BookOpen className="h-10 w-10 text-primary/30 mx-auto mb-3" />
                        <div className="font-display text-xl text-primary mb-1">
                            No programs yet
                        </div>
                        <div className="text-sm text-muted-foreground mb-6">
                            Pick a program from the catalog and start learning today.
                        </div>
                        <Button asChild className="bg-primary hover:bg-primary/90">
                            <Link to="/">Explore catalog</Link>
                        </Button>
                    </div>
                ) : (
                    <motion.div
                        variants={CONTAINER}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                        {sortedEnrollments.map((e) => (
                            <ProgramCard key={e.id} enrollment={e} />
                        ))}
                    </motion.div>
                )}
            </motion.section>
        </motion.div>
    );
}
